import axios, { AxiosError } from 'axios';
import i18n from 'i18next';

import { TranslationKeys } from '@ui-core/types';

import { flattenObject } from '..';

export const logError = (error: unknown) => console.error(error);

const extractLocalizationKeyFromMessage = (
  message: string,
  options?: {
    [key: string]: TranslationKeys;
  }
): TranslationKeys => {
  if (options && Object.keys(options).includes(message)) {
    return options[message];
  }

  const resources = i18n.getResourceBundle(
    i18n.resolvedLanguage!,
    'translation'
  ) as {
    [key: string]: string;
  };
  const listOfKeys = resources ? Object.keys(flattenObject(resources)) : [];
  if (listOfKeys.includes(message)) return message as TranslationKeys;

  return 'general.unexpectedError';
};

export const extractMessageFromError: (
  error: unknown,
  options?: {
    [key: string]: TranslationKeys;
  }
) => TranslationKeys = (error, options) => {
  logError(error);

  if (axios.isAxiosError<{ message: string }>(error)) {
    const potentialMessage = error.response?.data?.message;
    if (typeof potentialMessage === 'string') {
      return extractLocalizationKeyFromMessage(potentialMessage, options);
    }

    if (error.code === 'ERR_NETWORK') return 'general.networkError';
    return 'general.unexpectedError';
  }

  if (error instanceof Error) {
    const message: TranslationKeys = (error as { message: TranslationKeys })
      .message;

    return extractLocalizationKeyFromMessage(message, options);
  }

  return 'general.unexpectedError';
};

export class HTTPError extends Error {
  code: number;
  message: TranslationKeys;
  getData: <T>() => T;

  static readonly codes = {
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    UNEXPECTED: 500,
  };

  constructor(message: TranslationKeys, code?: number, data?: unknown) {
    super(message);
    this.message = message;
    this.code = code ?? HTTPError.codes.UNEXPECTED;
    this.getData = <T>() => data as T;
  }
}

export const createHTTPError = (error: unknown) => {
  if (error instanceof AxiosError)
    return new HTTPError(
      error.message as TranslationKeys,
      error.status,
      error.response?.data
    );
  if (!(error instanceof Error))
    return new HTTPError('general.unexpectedError');
  const message = extractMessageFromError(error);
  return new HTTPError(message);
};
